import { useState, useEffect } from 'react';
import { isProduction } from 'utils/environment';

/**
 * 🧪 TEMPORARY: Gallery Testing Override Hook
 * 
 * Allows forcing specific image counts via URL parameter for testing bento variants.
 * ONLY works in non-production environments for safety.
 * 
 * Usage:
 *   Add to URL: ?gallery_test_count=3
 *   Or use ModHeader: x-gallery-test-count: 3
 * 
 * Examples:
 *   ?gallery_test_count=0  -> Zero State
 *   ?gallery_test_count=1  -> Banner State
 *   ?gallery_test_count=2  -> Split State (2 images)
 *   ?gallery_test_count=3  -> Split State (3 images)
 *   ?gallery_test_count=4  -> Split State (4 images)
 *   ?gallery_test_count=5  -> Grid State (5 images)
 *   ?gallery_test_count=6  -> Grid State (6 images)
 *   ?gallery_test_count=7  -> Grid State (7+ images)
 * 
 * @returns Override count or undefined if no override is set or in production
 */
export function useGalleryTestOverride(): number | undefined {
  const [overrideCount, setOverrideCount] = useState<number | undefined>(undefined);

  useEffect(() => {
    // 🚨 SAFETY: Disable in production
    if (isProduction) {
      return;
    }

    if (typeof window === 'undefined') return;

    // Check URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    const urlOverride = urlParams.get('gallery_test_count');
    
    if (urlOverride !== null) {
      const parsed = parseInt(urlOverride, 10);
      if (!isNaN(parsed) && parsed >= 0 && parsed <= 100) {
        console.debug('[Gallery:Test] URL override active:', parsed);
        setOverrideCount(parsed);
        return;
      }
    }

    // Check ModHeader (would need to be passed via cookie or custom header handling)
    // For now, URL parameter is sufficient for testing
  }, []);

  return overrideCount;
}
